import { useState, useRef, useEffect } from 'react';
import Portal from '../atoms/Portal';
import { ChevronDown, Check } from 'lucide-react';

export default function Dropdown({
  label,
  options = [],
  value,
  onChange,
  placeholder = 'Select option...',
  className = '',
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 200 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const selectedOption = options.find((opt) => (typeof opt === 'object' ? opt.value === value : opt === value));
  const selectedLabel = selectedOption
    ? typeof selectedOption === 'object'
      ? selectedOption.label
      : selectedOption
    : placeholder;

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleSelectOption = (optValue) => {
    if (disabled) return;
    if (onChange) {
      onChange(optValue);
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(e.target);
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(e.target);
      
      if (isOutsideTrigger && isOutsidePopover) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className={`form-group ${className}${disabled ? ' is-disabled' : ''}`} ref={triggerRef}>
      {label && <label>{label}</label>}
      <div
        className={`custom-dropdown-trigger ${isOpen ? 'custom-dropdown-trigger--open' : ''} ${
          disabled ? 'custom-dropdown-trigger--disabled is-disabled' : ''
        }`}
        onClick={disabled ? undefined : toggleDropdown}
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
      >
        <span className={`custom-dropdown-trigger__label ${!selectedOption ? 'custom-dropdown-trigger__label--placeholder' : ''}`}>
          {selectedLabel}
        </span>
        <ChevronDown size={16} className="custom-dropdown-trigger__icon" />
      </div>

      {isOpen && (
        <Portal>
          <div
            ref={popoverRef}
            className="portal-popover custom-dropdown-menu"
            style={{
              top: coords.top,
              left: coords.left,
              width: coords.width,
            }}
          >
            {options.map((opt, idx) => {
              const optVal = typeof opt === 'object' ? opt.value : opt;
              const optLbl = typeof opt === 'object' ? opt.label : opt;
              const isSelected = value === optVal;
              return (
                <div
                  key={idx}
                  className={`dropdown-item ${isSelected ? 'dropdown-item--selected' : ''}`}
                  onClick={() => handleSelectOption(optVal)}
                >
                  <span>{optLbl}</span>
                  {isSelected && <Check size={14} />}
                </div>
              );
            })}
          </div>
        </Portal>
      )}
    </div>
  );
}
